import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { theme } from '@resistapp/client/components/shared/theme';
import { Loading } from '@resistapp/client/components/states/loading';
import { useAdminSensors, usePostAdminSensorsUpload } from '@resistapp/client/hooks/api';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { formatWorkbookTimestamp } from '@resistapp/client/utils/lab-workbook';
import {
  SENSOR_LABEL_BY_NAME,
  zSensorName,
  zSensorCsvTimeZone,
  type SensorCsvTimeZone,
  type AdminSensorUploadResponse,
  type SensorName,
} from '@resistapp/common/api-types';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

export function AdminSensorsView() {
  const [sensorName, setSensorName] = useState<SensorName>('dispensers');
  const [file, setFile] = useState<File | null>(null);
  const [timeZone, setTimeZone] = useState<SensorCsvTimeZone>('Europe/Helsinki');
  const [uploadResult, setUploadResult] = useState<AdminSensorUploadResponse | null>(null);
  const sensorsQuery = useAdminSensors();
  const uploadSensorData = usePostAdminSensorsUpload();
  const showToast = useToastWithoutDebounce();
  const queryClient = useQueryClient();

  if (sensorsQuery.error) {
    throw sensorsQuery.error instanceof Error ? sensorsQuery.error : new Error(String(sensorsQuery.error));
  }

  const uploadDisabled = !file || uploadSensorData.isPending;

  const handleUpload = async () => {
    if (!file) {
      return;
    }
    const formData = new FormData();
    formData.append('sensorName', sensorName);
    formData.append('timeZone', timeZone);
    formData.append('file', file);

    try {
      const result = await uploadSensorData.mutateAsync(formData);
      setUploadResult(result);
      await queryClient.invalidateQueries({ queryKey: ['/admin/sensors'] });
      showToast({
        title: 'Sensor data uploaded',
        description: `${result.insertedRows} new readings added for ${SENSOR_LABEL_BY_NAME[result.sensorName]}.`,
        status: 'success',
        duration: 5000,
      });
    } catch (error) {
      showToast({
        title: 'Sensor upload failed',
        description: error instanceof Error ? error.message : 'Please check the file and try again.',
        status: 'error',
        duration: 7000,
      });
    }
  };

  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <TitleBar title="Room sensors" $extraWide={true} />
      <VStack align="stretch" spacing={4} padding={theme.spacing[4]}>
        <Text>
          Chip uploads for Resistomap’s lab automatically download both room sensors in the background. CSV uploads can
          also fill gaps; existing readings are kept.
        </Text>
        <Box bg="white" borderRadius={6} padding={theme.spacing[3]} boxShadow="sm">
          <HStack align="flex-end" spacing={theme.spacing[4]} flexWrap="wrap">
            <FormControl width={{ base: '100%', md: '260px' }}>
              <FormLabel>Sensor</FormLabel>
              <Select
                value={sensorName}
                onChange={event => {
                  setSensorName(zSensorName.parse(event.target.value));
                }}
              >
                {zSensorName.options.map(name => (
                  <option key={name} value={name}>
                    {SENSOR_LABEL_BY_NAME[name]}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl width={{ base: '100%', md: '420px' }}>
              <FormLabel>CSV file</FormLabel>
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={event => {
                  setFile(event.target.files?.[0] ?? null);
                }}
              />
            </FormControl>
            <FormControl width={{ base: '100%', md: '220px' }}>
              <FormLabel>CSV time zone</FormLabel>
              <Select
                value={timeZone}
                onChange={event => {
                  setTimeZone(zSensorCsvTimeZone.parse(event.target.value));
                }}
              >
                <option value="Europe/Helsinki">Helsinki (EET/EEST)</option>
                <option value="UTC">UTC</option>
              </Select>
            </FormControl>
            <Button
              leftIcon={<FontAwesomeIcon icon={faUpload} />}
              isLoading={uploadSensorData.isPending}
              isDisabled={uploadDisabled}
              onClick={() => {
                void handleUpload();
              }}
            >
              Upload
            </Button>
          </HStack>
        </Box>

        {uploadResult ? (
          <Box bg="white" borderRadius={6} padding={theme.spacing[3]} boxShadow="sm">
            <HStack spacing={theme.spacing[6]} flexWrap="wrap">
              <Metric label="Parsed" value={uploadResult.parsedRows} />
              <Metric label="Inserted" value={uploadResult.insertedRows} />
              <Metric label="Before 2026" value={uploadResult.skippedBefore2026} />
              <Metric label="Already stored" value={uploadResult.skippedExisting} />
              <Metric label="Duplicate timestamps" value={uploadResult.skippedDuplicateInFile} />
              <Metric label="Latest" value={formatNullableTimestamp(uploadResult.latestMeasuredAt)} />
            </HStack>
          </Box>
        ) : null}

        <Box bg="white" borderRadius={6} padding={theme.spacing[3]} boxShadow="sm">
          {sensorsQuery.isPending ? (
            <Loading />
          ) : (
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Sensor</Th>
                  <Th isNumeric>Readings</Th>
                  <Th>Latest reading</Th>
                  <Th>Automatic sync</Th>
                  <Th>Last successful sync</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sensorsQuery.data.sensors.map(sensor => (
                  <Tr key={sensor.name}>
                    <Td>{sensor.label}</Td>
                    <Td isNumeric>{sensor.rowCount}</Td>
                    <Td>{formatNullableTimestamp(sensor.latestMeasuredAt)}</Td>
                    <Td>
                      {sensor.sync.status === 'IDLE' && !sensor.sync.lastSuccessAt
                        ? 'Waiting for chip upload'
                        : syncStatusLabel[sensor.sync.status]}
                      {sensor.sync.error ? (
                        <Text fontSize="sm" color="red.600">
                          {sensor.sync.error}
                        </Text>
                      ) : null}
                    </Td>
                    <Td>{formatNullableTimestamp(sensor.sync.lastSuccessAt)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </VStack>
    </div>
  );
}

const syncStatusLabel = {
  NOT_CONFIGURED: 'Connection not configured',
  IDLE: 'Up to date',
  PENDING: 'Queued',
  SYNCING: 'Downloading',
  ERROR: 'Failed — will retry',
};

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <VStack align="flex-start" spacing={0}>
      <Text fontSize="xs" color="gray.600">
        {label}
      </Text>
      <Text fontWeight={600}>{value}</Text>
    </VStack>
  );
}

function formatNullableTimestamp(value: Date | string | null): string {
  return value ? formatWorkbookTimestamp(value) : 'None';
}
